import { z } from 'zod';
import {
  ActionApprovalStatus,
  ActionStatus,
  SLAStatusEnum,
} from '@issuemanager/constants';
import { minimalUserSchema } from '../user';
import { govmapGroupWithParentNameSchema } from '../govmap-group';
import { optionalDate } from '../common';
import { actionAttachmentQueryResponseSchema } from '../action-attachment';

// 1-to-1 mapping with database columns
const actionTableSchema = z.object({
  id: z.string(),
  created_at: optionalDate(),
  updated_at: optionalDate(),
  approver_id: z.string().uuid().nullish(),
  issue_id: z.string().uuid().nullish(),
  title: z.string(),
  description: z.string().nullish(),
  status: z.nativeEnum(ActionStatus),
  owner_id: z.string().uuid().nullish(),
  due_date: optionalDate(),
  deleted_at: optionalDate(),
  slug_id: z.string().nullish(),
  approval_status: z
    .nativeEnum(ActionApprovalStatus)
    .default(ActionApprovalStatus.NOT_SET),
  decline_note: z.string().nullish(),
  sequence: z.number().nullable(),
});

type ActionTableSchemaType = z.infer<typeof actionTableSchema>;

// Common transformer for converting Action columns from snake_case to camelCase
const actionSchemaTransformer = (action: ActionTableSchemaType) => ({
  id: action.id,
  createdAt: action.created_at,
  updatedAt: action.updated_at,
  approverId: action.approver_id,
  issueId: action.issue_id,
  title: action.title,
  description: action.description,
  status: action.status,
  ownerId: action.owner_id,
  dueDate: action.due_date,
  deletedAt: action.deleted_at,
  slugId: action.slug_id,
  approvalStatus: action.approval_status,
  declineNote: action.decline_note,
  sequence: action.sequence,
});

export const actionSchema = actionTableSchema.transform(
  actionSchemaTransformer,
);

export type ActionSchemaType = z.infer<typeof actionSchema>;

export const actionQueryResponseSchema = actionTableSchema
  .extend({
    owner: minimalUserSchema.nullish(),
    approver: minimalUserSchema.nullish(),
    govmap_groups: z.array(govmapGroupWithParentNameSchema).nullable(),
    is_action_owner: z.boolean().nullish(),
    is_action_approver: z.boolean().nullish(),
    sla_status: z.nativeEnum(SLAStatusEnum).default(SLAStatusEnum.NO_PROBLEM),
    issue_code: z.string().nullish(),
    status: z.nativeEnum(ActionStatus),
    attachments: z.array(actionAttachmentQueryResponseSchema).optional(),
  })
  .transform((action) => ({
    ...actionSchemaTransformer(action),
    owner: action.owner,
    approver: action.approver,
    govmapGroups: action.govmap_groups,
    isActionOwner: action.is_action_owner,
    isActionApprover: action.is_action_approver,
    slaStatus: action.sla_status,
    issueCode: action.issue_code,
    attachments: action.attachments,
  }));

export type ActionQueryResponseSchemaType = z.infer<
  typeof actionQueryResponseSchema
>;
